// WorkItemsPage.jsx — SB-056: the member-readable work-item (BL-) view.
// Batch-41 friction: BL- numbers circulated in tickets and chat, but a member
// had NO page that could show what a BL- actually says — verifying an
// assignment meant asking the owner to read the registry aloud. This page reads
//   GET /api/me/backlogs?projectGroupId&stakeholder  (SB-056 extension)
// which now carries `body` and `isStakeholder`; rows are the same active set
// the work-hours picker uses (R-04 pending|in_progress, R-29 non-friction).
// Cards are accordions. A BL body IS long-form markdown source, and since batch 88
// it is rendered as such by <MarkdownBody> — the same renderer the publish pipeline
// uses, so one piece of markdown cannot look different here than in the docs.
import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { hasMoreNote } from '../api/paging.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { BACKLOG_PICKER_LIMIT } from './workDays.mjs';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import HelpLink from '../components/HelpLink.jsx';
import MarkdownBody from '../components/MarkdownBody.jsx';

function statusChip(t, status) {
  const k = `workItems.status.${status}`;
  const label = t(k) === k ? status : t(k);
  return (
    <Chip
      size="small"
      variant="outlined"
      color={status === 'in_progress' ? 'primary' : 'default'}
      label={label}
    />
  );
}

export default function WorkItemsPage() {
  const t = useT();
  const { lang } = useLang();
  // 第一百零一批（多项目制）：组来自会话上下文（切换器切到哪组，这页跟到哪组），
  // 不再各自取第一项——那正是单组时代的化石（SB-042 注释里点名的三页之一）。
  const { currentGroup } = useAuth();
  const [onlyMine, setOnlyMine] = useState(false);

  const load = useLoad(async () => {
    // This surface needs the real integer group id — no slug fallback is
    // possible, so a missing group is a rendered error state.
    if (!currentGroup || currentGroup.id == null) return { noGroup: true, backlogs: [] };
    // SB-159（第三百八十二批）：页长取服务端上限（500），并把 hasMore 带出来——超过一页时说出来，
    // 而不是让「列出的这些」冒充「全部活跃的工作项」。
    const r = await api.myBacklogs(currentGroup.id, { stakeholder: onlyMine, lang, limit: BACKLOG_PICKER_LIMIT });
    return { noGroup: false, backlogs: Array.isArray(r?.backlogs) ? r.backlogs : [], page: r };
  }, [onlyMine, lang, currentGroup?.id]);

  const backlogs = load.data?.backlogs ?? [];
  const moreNote = load.status === 'ready' ? hasMoreNote(load.data?.page) : null;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography sx={{ fontWeight: 700 }} variant="h5" component="h1">
          {t('workItems.title')}
          <HelpLink section="work" />
        </Typography>
        <FormControlLabel
          control={<Switch checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />}
          label={t('workItems.onlyMine')}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('workItems.subtitle')}
      </Typography>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { fallback: 'workItems.error' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>
            {t('common.retry')}
          </Button>
        </Box>
      ) : load.data?.noGroup ? (
        <Alert severity="warning">{t('workItems.noGroup')}</Alert>
      ) : (
        <Box>
          {/* SB-159 复审：「还有更多没列出来」在空列表上同样要说——「只看与我相关」时这一页一个都没有、
              服务端却说还有（旧后端先截 500 再按关联方筛），那句空态「没有与你相关的工作项」就是一句假话。 */}
          {moreNote ? (
            <Alert severity="warning" sx={{ mb: 2 }} data-paging-notice={moreNote.key}>
              {t(moreNote.key, moreNote.params)}
            </Alert>
          ) : null}
          {backlogs.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {onlyMine ? t('workItems.emptyFiltered') : t('workItems.empty')}
              </Typography>
            </Paper>
          ) : backlogs.map((b) => (
            <Accordion key={b.no} variant="outlined" disableGutters>
              {/* Text caret — no @mui/icons-material dependency in this project. */}
              <AccordionSummary
                expandIcon={<Box component="span" sx={{ fontSize: 14 }}>▾</Box>}
                sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ alignItems: 'center', flexWrap: 'wrap', pr: 1, minWidth: 0 }}
                >
                  <Typography sx={{ fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>
                    {b.no}
                  </Typography>
                  {statusChip(t, b.status)}
                  {b.isStakeholder ? (
                    <Chip size="small" color="secondary" label={t('workItems.mineChip')} />
                  ) : null}
                  {/* OQ-A19 ruled fallback: no translation yet → original text
                      with an honest source-language badge. */}
                  {b.resolvedLang && b.resolvedLang !== lang ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={t('workItems.untranslated', { lang: b.sourceLang })}
                    />
                  ) : null}
                  {/* 第九十八批（需求方实报 EN 条目推挤）：flex 基准 14rem 是「提早换行」
                      的本体——flex 布局先收缩后换行，minWidth:0 的标题会被压成细条
                      （360px 实测 144px 宽、7 行高塔）而不触发 wrap；给了基准宽，
                      挤不下就整行下移到全宽，长无断点词再由 overflowWrap 兜底。
                      桌面宽度充裕，行为不变。 */}
                  <Typography sx={{ minWidth: 0, flex: '1 1 14rem', overflowWrap: 'anywhere' }}>
                    {b.title}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <MarkdownBody body={b.body} format={b.bodyFormat} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
